/* =========================================================
   AF — Overlay Facets (Filters) — Menu-parity skin
   Fixes:
   1) Hard scroll lock (body fixed) + overscroll containment
   2) Sort renders as checkbox-style list (not dropdown)
   3) Price connecting track hardened (vars + repaint) — NOW rAF (no stutter)
   4) No layout shift: remove overlay scrollbar, move scrolling to columns
      + Horizontal progress bar in header replaces the divider
      + Wheel routing: scroll active column even if mouse is not on it
   CRITICAL FIXES (this revision):
   A) Progress bar: track ANY scroll within overlay via CAPTURE scroll listener
      + auto-create progress DOM if missing (no markup mismatch failures)
   B) Mobile/browser back gesture: handled via History API
   C) Wheel edge containment: prevent page scroll when scroller hits top/bottom
   D) Price drag stutter: remove forced reflow during drag, update track via rAF
   E) Failsafe unlock: if lock class ever sticks, hardUnlockScroll clears it
   ✅ PATCH (this revision):
   - Prevent "first scroll jump" by NOT touching native lock classes on load
   - Track whether this script ever locked scroll
   - Allow hardUnlockScroll(force=true) right before opening overlay
   ✅ UPGRADE (this revision, no core logic change):
   - Safer History handling:
     • Only call history.back() when current history.state is ours
     • popstate always closes subdrawer first, then overlay (same UX)
     • prevents accidental navigation if other scripts pushState while overlay is open
   ✅ FIX (this revision):
   - Subdrawer back button no longer closes the entire overlay
     • Uses _suppressNextPop flag so programmatic history.back() from UI
       back button doesn't cascade into closing the overlay
   - Fixed querySelector tagged-template-literal syntax errors
   ⚡ PERF + UX UPGRADE (this revision):
   - Video play/pause management on open/close
   - Hover intent delay configurable (100ms default)
   - Swipe-right-to-close gesture on mobile subdrawer
   ========================================================= */
(() => {
  "use strict";
  const root = document.getElementById("af-overlay-facets-root");
  if (!root) return;
  if (root.__afFacetsInit) return;
  root.__afFacetsInit = true;
  const OPEN_CLASS = "is-open";
  const LOCK_CLASS = "af-facets-lock";
  const HAS_ACTIVE_CLASS = "af-of--has-active-submenu";
  const SUBOPEN_CLASS = "af-of--subopen";
  const HIDE_DELAY_MS = 420;
  const HIST_TOKEN = `af_facets_${Math.random().toString(36).slice(2)}`;

  /* ⚡ Configurable performance settings */
  const cfg = {
    hoverIntentMs: 100,  // was instant — prevents accidental submenu triggers
  };

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const nodes = {
    nav: $("[data-af-facets-nav]", root),
    subnav: $("[data-af-facets-subnav]", root),
    close: $("[data-af-facets-close]", root),
    clearAll: $("[data-af-facets-clearall]", root),
    apply: $("[data-af-facets-apply]", root),
    summary: $("[data-af-facets-summary]", root),
    chips: $("[data-af-facets-chips]", root),
    priceTopbar: $("[data-af-price-topbar]", root),
    topbar: $(".af-of__top", root),
    progWrap: $("[data-af-scrollprog], .af-of__scrollProg", root),
    progFill: $("[data-af-scrollprogfill], .af-of__scrollProgFill", root),
    quickbar: $("[data-af-facets-quick]", root),
    quickChips: $("[data-af-facets-quickchips]", root),
    forYou: $("[data-af-facets-foryou]", root),
    forYouInner: $("[data-af-facets-foryou-inner]", root),
  };
  const state = {
    hideT: 0,
    activeKey: null,
    form: null,
    facets: [],
    priceFacet: null,
    seeded: false,
    // progress
    activeScrollEl: null,
    scrollBound: false,
    rafProg: 0,
    lastScrollTarget: null,
    // History
    histLevel: 0,
    histArmed: false,
    // a11y
    returnFocusEl: null,
  };

  /* ✅ FIX: suppress flag to prevent popstate from cascading
     when we programmatically call history.back() from the
     subdrawer UI back button */
  let _suppressNextPop = false;

  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  const isOpen = () => root.classList.contains(OPEN_CLASS);
  const isMobile = () => window.matchMedia("(max-width: 989px)").matches;

  /* =========================================================
     Focus trap — armed only while open (mirrors af-overlay-menu.js)
  ========================================================= */
  const FOCUS_SEL =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const getFocusableEls = () =>
    $$(FOCUS_SEL, root).filter(
      (el) => el && el.getClientRects && el.getClientRects().length > 0
    );
  const trapTabKey = (e) => {
    if (e.key !== "Tab" || !isOpen()) return;
    const focusables = getFocusableEls();
    if (!focusables.length) return;
    const active = document.activeElement;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!root.contains(active)) {
      e.preventDefault();
      first.focus({ preventScroll: true });
      return;
    }
    if (e.shiftKey) {
      if (active === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus({ preventScroll: true });
    }
  };

  /* =========================================================
     ⚡ Video management — play on open, pause on close
  ========================================================= */
  const playPromoVideos = () => {
    root.querySelectorAll("video.af-of__promoVideo").forEach((v) => {
      try {
        v.preload = "metadata";
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch {}
    });
  };

  const pausePromoVideos = () => {
    root.querySelectorAll("video.af-of__promoVideo").forEach((v) => {
      try { v.pause(); } catch {}
    });
  };

  /* =========================================================
     ⚡ Mobile swipe-right-to-close gesture for subdrawer
  ========================================================= */
  const initSwipeGesture = (el) => {
    if (!el || el.dataset.afSwipeBound) return;
    el.dataset.afSwipeBound = "1";
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const SWIPE_THRESHOLD = 60;
    const ANGLE_LIMIT = 30;

    el.addEventListener("touchstart", (e) => {
      if (!root.classList.contains(SUBOPEN_CLASS)) return;
      const touch = e.touches[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = startX < window.innerWidth * 0.4;
    }, { passive: true });

    el.addEventListener("touchend", (e) => {
      if (!tracking || !root.classList.contains(SUBOPEN_CLASS)) return;
      tracking = false;

      const touch = e.changedTouches[0];
      if (!touch) return;

      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const angle = Math.abs(Math.atan2(dy, dx) * (180 / Math.PI));

      if (dx > SWIPE_THRESHOLD && angle < ANGLE_LIMIT) {
        closeSubdrawerInternal(false);
      }
    }, { passive: true });
  };

  /* ---------------------------
     Ensure progress DOM exists
     --------------------------- */
  const ensureProgressEls = () => {
    if (!nodes.topbar) return;
    let wrap = nodes.progWrap;
    let fill = nodes.progFill;
    if (!wrap || !fill) {
      wrap = document.createElement("div");
      wrap.className = "af-of__scrollProg";
      wrap.setAttribute("data-af-scrollprog", "");
      fill = document.createElement("span");
      fill.className = "af-of__scrollProgFill";
      fill.setAttribute("data-af-scrollprogfill", "");
      wrap.appendChild(fill);
      nodes.topbar.appendChild(wrap);
    }
    nodes.progWrap = wrap;
    nodes.progFill = fill;
    nodes.progWrap.removeAttribute("hidden");
  };
  /* ---------------------------
     HISTORY helpers (safer)
     --------------------------- */
  const isOurHistState = (st) => !!(st && st.__af === HIST_TOKEN);
  const currentHistIsOursAtOrAbove = (level) => {
    try {
      const st = history.state;
      return isOurHistState(st) && Number(st.level || 0) >= level;
    } catch {
      return false;
    }
  };
  const pushHist = (level) => {
    if (!isOpen()) return;
    try {
      history.pushState({ __af: HIST_TOKEN, level }, "");
      state.histLevel = level;
      state.histArmed = true;
    } catch {
      state.histLevel = Math.max(state.histLevel, level);
      state.histArmed = true;
    }
  };
  const requestBackOne = () => {
    if (!state.histArmed) return false;
    try {
      history.back();
      return true;
    } catch {
      return false;
    }
  };
  /* ---------------------------
     Subdrawer open/close
     ✅ FIX: set _suppressNextPop before calling history.back()
     so the popstate handler knows NOT to close the overlay
     --------------------------- */
  const closeSubdrawerInternal = (fromPop) => {
    root.classList.remove(SUBOPEN_CLASS);
    state.activeKey = null;
    if (nodes.nav) {
      $$("[data-af-facet-key]", nodes.nav).forEach((b) =>
        b.classList.remove("is-active")
      );
    }
    if (nodes.subnav) {
      nodes.subnav.innerHTML = '<div class="af-of__subEmpty">Select a filter</div>';
    }
    pickBestScrollerAndUpdate();
    // ✅ FIX: when closing subdrawer from UI (not from popstate),
    // set suppress flag so the resulting popstate doesn't also close the overlay
    if (!fromPop && currentHistIsOursAtOrAbove(2)) {
      _suppressNextPop = true;
      requestBackOne();
    }
  };
  const openSubdrawerInternal = () => {
    if (!isMobile()) return;
    if (root.classList.contains(SUBOPEN_CLASS)) return;
    root.classList.add(SUBOPEN_CLASS);
    if (state.histLevel < 2) pushHist(2);
    pickBestScrollerAndUpdate();
  };
  /* ---------------------------
     popstate (mobile back gesture)
     ✅ FIX: check _suppressNextPop first — if set, the popstate
     was triggered by our own programmatic history.back() from the
     subdrawer back button, so just consume it and stay on the overlay.
     --------------------------- */
  const onPopState = (e) => {
    if (!isOpen()) return;

    // ✅ FIX: if we triggered this popstate ourselves (subdrawer back button),
    // just consume it — subdrawer is already closed, overlay stays open.
    if (_suppressNextPop) {
      _suppressNextPop = false;
      state.histLevel = 1;
      state.histArmed = true;
      return;
    }

    // If subdrawer is open (browser back gesture), close subdrawer first
    if (root.classList.contains(SUBOPEN_CLASS)) {
      closeSubdrawerInternal(true);
      state.histLevel = 1;
      state.histArmed = true;
      return;
    }
    // If overlay open and back occurs, close overlay.
    closeInternal(true);
    // If the new state is ours, update level; otherwise consider history disarmed.
    const st = e && e.state;
    if (isOurHistState(st)) {
      state.histLevel = Number(st.level || 0);
      state.histArmed = true;
    } else {
      state.histLevel = 0;
      state.histArmed = false;
    }
  };
  window.addEventListener("popstate", onPopState);
  /* ---------------------------
     HARD scroll lock (body fixed)
     --------------------------- */
  let __scrollY = 0;
  let __scrollX = 0;
  let __padRight = 0;
  let __afScrollLocked = false;
  let __afEverLocked = false;
  const getScrollbarW = () => {
    try {
      return window.innerWidth - document.documentElement.clientWidth;
    } catch {
      return 0;
    }
  };
  const lockScroll = () => {
    if (__afScrollLocked) return;
    __scrollY = window.scrollY || window.pageYOffset || 0;
    __scrollX = window.scrollX || window.pageXOffset || 0;
    const sbw = getScrollbarW();
    __padRight = sbw > 0 ? sbw : 0;
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.classList.add(LOCK_CLASS);
    if (__padRight) document.body.style.paddingRight = `${__padRight}px`;
    // NO position:fixed — it resets window.scrollY to 0 which breaks
    // GA4 scroll-depth tracking and Meta engagement metrics.
    // overflow:hidden + touch-action:none is sufficient on modern browsers.
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    __afScrollLocked = true;
    __afEverLocked = true;
  };
  const unlockScroll = () => {
    if (!__afScrollLocked) return;
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.classList.remove(LOCK_CLASS);
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
    document.body.style.paddingRight = "";
    document.body.style.touchAction = "";
    __scrollY = 0;
    __scrollX = 0;
    __padRight = 0;
    __afScrollLocked = false;
  };
  /* ---------------------------
     Kill Dawn native drawer locks
     --------------------------- */
  const NATIVE_LOCK_CLASSES = [
    "overflow-hidden",
    "scroll-locked",
    "no-scroll",
    "lock-scroll",
  ];
  const closeNativeMobileDetails = () => {
    const openDetails = document.querySelectorAll(
      "details.mobile-facets__disclosure[open], menu-drawer details[open]"
    );
    openDetails.forEach((d) => {
      try {
        d.open = false;
      } catch {}
      d.removeAttribute("open");
    });
  };
  const hardUnlockScroll = (force = false) => {
    if (!force && !isOpen() && !__afEverLocked) return;
    document.body.classList.remove(LOCK_CLASS);
    document.documentElement.classList.remove(LOCK_CLASS);
    if (force || isOpen() || __afEverLocked) {
      NATIVE_LOCK_CLASSES.forEach((c) => {
        document.body.classList.remove(c);
        document.documentElement.classList.remove(c);
      });
    }
    if (!isOpen()) {
      if (__afScrollLocked) {
        unlockScroll();
        return;
      }
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.style.touchAction = "";
    }
  };
  /* ---------------------------
     Dawn hooks
     --------------------------- */
  const getDawnFacetForm = () =>
    document.querySelector(
      "#FacetFiltersForm, form[id*='FacetFiltersForm'], facets-form form"
    );
  const getDawnSortSelect = () =>
    document.querySelector(
      "select#SortBy, select[name='sort_by'], facet-filters-form select[name='sort_by']"
    );
  // Params that scope the page (search terms, a brand's vendors page), not
  // filters. Apply and Clear must carry them, or /search?q=oud and
  // /collections/vendors?q=Creed fall back to every product.
  const SCOPE_PARAMS = ["q", "type", "options[prefix]"];
  const getScopeParams = () => {
    const cur = new URLSearchParams(window.location.search);
    return SCOPE_PARAMS.filter((k) => cur.has(k)).map((k) => [k, cur.get(k)]);
  };
  /* ---------------------------
     Detached form
     ✅ FIX: corrected querySelector syntax (was tagged template literal)
     --------------------------- */
  const ensureDetachedForm = () => {
    if (state.form) return state.form;
    const f = document.createElement("form");
    f.method = "get";
    f.action = window.location.pathname;
    f.className = "af-of__pendingForm";
    f.hidden = true;
    root.appendChild(f);
    state.form = f;
    return f;
  };
  const ensureHiddenInput = (name, initialValue) => {
    const f = ensureDetachedForm();
    let i = f.querySelector('input[type="hidden"][name="' + CSS.escape(name) + '"]');
    if (!i) {
      i = document.createElement("input");
      i.type = "hidden";
      i.name = name;
      i.value = initialValue ?? "";
      state.form.appendChild(i);
    }
    return i;
  };
  const refreshOptionSelectedStates = () => {
    if (!state.form) return;
    const optBtns = root.querySelectorAll(
      ".af-of__optBtn[data-af-name][data-af-value]"
    );
    optBtns.forEach((b) => {
      const name = b.getAttribute("data-af-name");
      const value = b.getAttribute("data-af-value");
      const exists = !!state.form.querySelector(
        'input[name="' + CSS.escape(name) + '"][value="' + CSS.escape(value) + '"]'
      );
      b.classList.toggle("is-selected", exists);
      b.setAttribute("aria-pressed", String(exists));
    });
    const sortBtns = root.querySelectorAll(
      ".af-of__sortBtn[data-af-sort-value]"
    );
    sortBtns.forEach((b) => {
      const v = b.getAttribute("data-af-sort-value") || "";
      const n = b.getAttribute("data-af-sort-name") || "sort_by";
      const hid = state.form.querySelector('input[name="' + CSS.escape(n) + '"]');
      b.classList.toggle("is-selected", (hid?.value || "") === v);
      b.setAttribute("aria-pressed", String((hid?.value || "") === v));
    });
    const quickBtns = root.querySelectorAll(
      ".af-of__quickChip[data-af-name][data-af-value]"
    );
    quickBtns.forEach((b) => {
      const name = b.getAttribute("data-af-name");
      const value = b.getAttribute("data-af-value");
      const on = !!state.form.querySelector(
        'input[name="' + CSS.escape(name) + '"][value="' + CSS.escape(value) + '"]'
      );
      b.classList.toggle("is-selected", on);
      b.setAttribute("aria-pressed", String(on));
    });
  };
  /* ---------------------------
     Helpers
     --------------------------- */
  const toNumber = (v) => {
    const s = String(v ?? "").replace(/[^0-9.]/g, "");
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };
  // The store's Dawn filter values are lowercase slugs (e.g. "eau-de-parfum",
  // "for-everyone"). Prettify them for display only — the raw value is still
  // used for the form. Keeps small connector words lowercase.
  const PRETTY_SMALL = new Set(["de", "of", "the", "and", "la", "le", "du", "des", "di", "da"]);
  const prettyLabel = (s) => {
    const str = String(s || "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
    if (!str) return str;
    // Leave labels that already contain spaces + mixed case alone (already human).
    if (/\s/.test(String(s)) && /[A-Z]/.test(String(s))) return String(s).trim();
    return str
      .split(" ")
      .map((w, i) => {
        const lw = w.toLowerCase();
        if (i > 0 && PRETTY_SMALL.has(lw)) return lw;
        return lw.charAt(0).toUpperCase() + lw.slice(1);
      })
      .join(" ");
  };
  const getCleanLabelAndCount = (labEl) => {
    if (!labEl) return { label: "", count: "" };
    const ariaTextEl =
      labEl.querySelector('.facet-checkbox__text[aria-hidden="true"]') ||
      labEl.querySelector(".facet-checkbox__text-label") ||
      labEl.querySelector('[aria-hidden="true"]') ||
      labEl;
    const raw = (ariaTextEl.textContent || "").trim().replace(/\s+/g, " ");
    const m = raw.match(/\(([^)]+)\)\s*$/);
    const count = m ? String(m[1]).trim() : "";
    const label = raw.replace(/\s*\([^)]+\)\s*$/, "").trim();
    return { label: label || raw, count };
  };
  /* ---------------------------
     Parse Dawn facets
     ✅ FIX: corrected querySelector syntax throughout
     --------------------------- */
  const parseDawnFacets = () => {
    const form = getDawnFacetForm();
    const sortSelect = getDawnSortSelect();
    const facets = [];
    let priceFacet = null;
    if (sortSelect) {
      facets.push({
        key: "__sort__",
        type: "sort",
        label: "Sort by",
        sort: {
          name: sortSelect.name || "sort_by",
          value: sortSelect.value || "",
          options: Array.from(sortSelect.options || []).map((o) => ({
            value: o.value,
            label: o.textContent || o.label || o.value,
          })),
        },
      });
    }
    if (form) {
      const details = Array.from(
        form.querySelectorAll("details, .facets__disclosure")
      );
      details.forEach((d, idx) => {
        const sum = d.querySelector("summary, .facets__summary");
        const label =
          (sum && (sum.textContent || "").trim()) ||
          d.getAttribute("data-filter-label") ||
          `Filter ${idx + 1}`;
        const minInput = d.querySelector(
          'input[name*="filter.v.price.gte"], input[name*="filter.v.price.min"], input[name*="filter.v.price.greater_than"]'
        );
        const maxInput = d.querySelector(
          'input[name*="filter.v.price.lte"], input[name*="filter.v.price.max"], input[name*="filter.v.price.less_than"]'
        );
        if (minInput && maxInput) {
          const rangeMin = toNumber(minInput.min) ?? 0;
          const rangeMax =
            toNumber(maxInput.max) ??
            toNumber(maxInput.getAttribute("max")) ??
            toNumber(maxInput.placeholder) ??
            toNumber(maxInput.value) ??
            0;
          const step = toNumber(minInput.step) ?? toNumber(maxInput.step) ?? 1;
          priceFacet = {
            key: "__price__",
            type: "price",
            label: "Price",
            price: {
              minName: minInput.name,
              maxName: maxInput.name,
              minValue: minInput.value,
              maxValue: maxInput.value,
              rangeMin,
              rangeMax,
              step,
            },
          };
          return;
        }
        const inputs = Array.from(
          d.querySelectorAll('input[type="checkbox"], input[type="radio"]')
        ).filter((i) => i.name && i.value);
        if (!inputs.length) return;
        const opts = inputs.map((i) => {
          const id = i.id;
          const labEl =
            (id && form.querySelector('label[for="' + CSS.escape(id) + '"]')) ||
            i.closest("label");
          const cleaned = getCleanLabelAndCount(labEl);
          return {
            name: i.name,
            value: i.value,
            label: prettyLabel(cleaned.label || i.value),
            checked: i.checked,
            count: cleaned.count || "",
          };
        });
        facets.push({
          key: `f_${idx}_${label.toLowerCase().replace(/\W+/g, "_")}`,
          type: "options",
          label,
          options: opts,
        });
      });
    }
    state.facets = facets;
    state.priceFacet = priceFacet;
    ensureDetachedForm();
    state.form.innerHTML = "";
    getScopeParams().forEach(([k, v]) => ensureHiddenInput(k, v));
    if (sortSelect)
      ensureHiddenInput(sortSelect.name || "sort_by", sortSelect.value || "");
    if (priceFacet) {
      ensureHiddenInput(priceFacet.price.minName, priceFacet.price.minValue || "");
      ensureHiddenInput(priceFacet.price.maxName, priceFacet.price.maxValue || "");
    }
    facets.forEach((f) => {
      if (f.type !== "options") return;
      f.options.forEach((o) => {
        if (o.checked) {
          const i = document.createElement("input");
          i.type = "hidden";
          i.name = o.name;
          i.value = o.value;
          state.form.appendChild(i);
        }
      });
    });
    state.seeded = true;
  };
  /* ---------------------------
     Render nav
     ⚡ Hover intent delay (desktop only)
     --------------------------- */
  let hoverTimer = 0;

  const bindDesktopHoverOpen = () => {
    const canHover = window.matchMedia("(hover:hover) and (pointer:fine)").matches;
    if (!canHover || !nodes.nav) return;
    const btns = $$("[data-af-facet-key]", nodes.nav);
    btns.forEach((b) => {
      b.addEventListener("mouseenter", () => {
        const key = b.getAttribute("data-af-facet-key");
        if (!key) return;
        window.clearTimeout(hoverTimer);
        hoverTimer = window.setTimeout(() => renderFacetIntoSubnav(key), cfg.hoverIntentMs);
      });
      b.addEventListener("mouseleave", () => {
        window.clearTimeout(hoverTimer);
      });
      b.addEventListener("focus", () => {
        const key = b.getAttribute("data-af-facet-key");
        if (key) renderFacetIntoSubnav(key);
      });
    });
  };
  const renderNav = () => {
    if (!nodes.nav) return;
    const ul = document.createElement("ul");
    ul.className = "af-of__navList";
    state.facets.forEach((f) => {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "af-of__navItemBtn";
      btn.setAttribute("data-af-facet-key", f.key);
      btn.innerHTML = `<span>${esc(f.label)}</span><span class="af-of__chev" aria-hidden="true">›</span>`;
      li.appendChild(btn);
      ul.appendChild(li);
    });
    nodes.nav.innerHTML = "";
    nodes.nav.appendChild(ul);
    bindDesktopHoverOpen();
  };
  /* ---------------------------
     Summary chips
     ✅ FIX: corrected querySelector syntax
     --------------------------- */
  const syncSummaryChips = () => {
    if (!nodes.summary || !nodes.chips) return;
    const chips = [];
    if (state.priceFacet && state.form) {
      const minName = state.priceFacet.price.minName;
      const maxName = state.priceFacet.price.maxName;
      const a =
        (state.form.querySelector('input[name="' + CSS.escape(minName) + '"]') || {}).value || "";
      const b =
        (state.form.querySelector('input[name="' + CSS.escape(maxName) + '"]') || {}).value || "";
      if (a || b) {
        chips.push({
          key: "__price__",
          label: `PRICE ${a || "…"} — ${b || "…"}`,
          remove: () => {
            ensureHiddenInput(minName, "").value = "";
            ensureHiddenInput(maxName, "").value = "";
          },
        });
      }
    }
    const hids = state.form
      ? Array.from(state.form.querySelectorAll('input[type="hidden"]'))
      : [];
    const seen = new Set();
    hids.forEach((i) => {
      if (!i.name) return;
      if (i.name.includes("sort_by") || SCOPE_PARAMS.includes(i.name)) return;
      if (
        state.priceFacet &&
        (i.name === state.priceFacet.price.minName ||
          i.name === state.priceFacet.price.maxName)
      )
        return;
      const key = `${i.name}::${i.value}`;
      if (seen.has(key)) return;
      seen.add(key);
      const chipLabel = i.name.includes("availability")
        ? (i.value === "1" ? "In stock" : "Out of stock")
        : prettyLabel(i.value);
      chips.push({
        key,
        label: chipLabel,
        remove: () => {
          const el = state.form.querySelector(
            'input[name="' + CSS.escape(i.name) + '"][value="' + CSS.escape(i.value) + '"]'
          );
          if (el) el.remove();
        },
      });
    });
    nodes.chips.innerHTML = "";
    chips.forEach((c) => {
      const chip = document.createElement("div");
      chip.className = "af-of__chip";
      chip.innerHTML = `<span>${esc(c.label)}</span>`;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "af-of__chipBtn";
      btn.setAttribute("aria-label", `Remove ${c.label}`);
      btn.innerHTML = "×";
      btn.addEventListener("click", () => {
        c.remove();
        refreshOptionSelectedStates();
        syncSummaryChips();
        renderPriceTopbar();
        pickBestScrollerAndUpdate();
      });
      chip.appendChild(btn);
      nodes.chips.appendChild(chip);
    });
    nodes.summary.hidden = chips.length === 0;
  };
  /* ---------------------------
     Price topbar
     --------------------------- */
  const renderPriceTopbar = () => {
    if (!nodes.priceTopbar) return;
    nodes.priceTopbar.innerHTML = "";
    if (!state.priceFacet) {
      nodes.priceTopbar.hidden = true;
      return;
    }
    nodes.priceTopbar.hidden = false;
    const f = state.priceFacet.price;
    const hidMin = ensureHiddenInput(f.minName, f.minValue || "");
    const hidMax = ensureHiddenInput(f.maxName, f.maxValue || "");
    const rangeMin = Number.isFinite(f.rangeMin) ? f.rangeMin : 0;
    const rangeMax = Number.isFinite(f.rangeMax) ? f.rangeMax : 0;
    const step = Number.isFinite(f.step) && f.step > 0 ? f.step : 1;
    const getCurrentMin = () => {
      const n = toNumber(hidMin.value);
      return n === null ? rangeMin : n;
    };
    const getCurrentMax = () => {
      const n = toNumber(hidMax.value);
      return n === null ? rangeMax : n;
    };
    const bar = document.createElement("div");
    bar.className = "af-of__priceBar";
    const tag = document.createElement("div");
    tag.className = "af-of__priceTag";
    tag.textContent = "Price";
    const fields = document.createElement("div");
    fields.className = "af-of__priceFields";
    const minField = document.createElement("div");
    minField.className = "af-of__priceField";
    const maxField = document.createElement("div");
    maxField.className = "af-of__priceField";
    const min = document.createElement("input");
    min.className = "af-of__priceInput";
    min.type = "number";
    min.inputMode = "numeric";
    min.placeholder = "Min";
    min.setAttribute("aria-label", "Minimum price");
    min.value = hidMin.value || "";
    const max = document.createElement("input");
    max.className = "af-of__priceInput";
    max.type = "number";
    max.inputMode = "numeric";
    max.placeholder = "Max";
    max.setAttribute("aria-label", "Maximum price");
    max.value = hidMax.value || "";
    const sep = document.createElement("div");
    sep.className = "af-of__priceSep";
    sep.setAttribute("aria-hidden", "true");
    sep.textContent = "—";
    minField.appendChild(min);
    maxField.appendChild(max);
    fields.appendChild(minField);
    fields.appendChild(sep);
    fields.appendChild(maxField);
    const slider = document.createElement("div");
    slider.className = "af-of__priceSlider";
    const track = document.createElement("div");
    track.className = "af-of__priceTrack";
    const rMin = document.createElement("input");
    rMin.type = "range";
    rMin.className = "af-of__range af-of__rangeMin";
    rMin.min = String(rangeMin);
    rMin.max = String(rangeMax);
    rMin.step = String(step);
    rMin.setAttribute("aria-label", "Minimum price");
    const rMax = document.createElement("input");
    rMax.type = "range";
    rMax.className = "af-of__range af-of__rangeMax";
    rMax.min = String(rangeMin);
    rMax.max = String(rangeMax);
    rMax.step = String(step);
    rMax.setAttribute("aria-label", "Maximum price");
    slider.appendChild(track);
    slider.appendChild(rMin);
    slider.appendChild(rMax);
    const clear = document.createElement("button");
    clear.type = "button";
    clear.className = "af-of__priceClear";
    clear.setAttribute("aria-label", "Clear price");
    clear.innerHTML = '<span aria-hidden="true">×</span>';
    const syncClear = () => {
      clear.hidden = !(hidMin.value || hidMax.value);
    };
    const clampPair = (a, b) => {
      let minV = a;
      let maxV = b;
      if (minV < rangeMin) minV = rangeMin;
      if (maxV > rangeMax) maxV = rangeMax;
      if (minV > maxV) minV = maxV;
      return [minV, maxV];
    };
    let trackRAF = 0;
    let pendingA = rangeMin;
    let pendingB = rangeMax;
    const paintTrack = () => {
      trackRAF = 0;
      const denom = rangeMax - rangeMin;
      const p1 = denom === 0 ? 0 : ((pendingA - rangeMin) / denom) * 100;
      const p2 = denom === 0 ? 100 : ((pendingB - rangeMin) / denom) * 100;
      const c1 = Math.max(0, Math.min(100, p1));
      const c2 = Math.max(0, Math.min(100, p2));
      slider.style.setProperty("--p1", `${c1.toFixed(3)}%`);
      slider.style.setProperty("--p2", `${c2.toFixed(3)}%`);
    };
    const updateTrack = (a, b) => {
      pendingA = a;
      pendingB = b;
      if (trackRAF) return;
      trackRAF = requestAnimationFrame(paintTrack);
    };
    const writeHiddenFromPair = (a, b) => {
      hidMin.value = min.value.trim() === "" ? "" : String(a);
      hidMax.value = max.value.trim() === "" ? "" : String(b);
    };
    const syncAllFromHidden = () => {
      const a = getCurrentMin();
      const b = getCurrentMax();
      const [aa, bb] = clampPair(a, b);
      rMin.value = String(aa);
      rMax.value = String(bb);
      min.value = hidMin.value || "";
      max.value = hidMax.value || "";
      updateTrack(aa, bb);
      syncClear();
    };
    let isDragging = false;
    const onRangeInput = () => {
      isDragging = true;
      let a = Number(rMin.value);
      let b = Number(rMax.value);
      if (a > b) {
        a = b;
        rMin.value = String(a);
      }
      if (b < a) {
        b = a;
        rMax.value = String(b);
      }
      min.value = String(a);
      max.value = String(b);
      updateTrack(a, b);
    };
    const onRangeEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      const a = Number(rMin.value);
      const b = Number(rMax.value);
      hidMin.value = String(a);
      hidMax.value = String(b);
      syncClear();
      syncSummaryChips();
      pickBestScrollerAndUpdate();
    };
    const onTextInput = (which) => {
      const av = toNumber(min.value);
      const bv = toNumber(max.value);
      const a = av === null ? rangeMin : av;
      const b = bv === null ? rangeMax : bv;
      const [aa, bb] = clampPair(a, b);
      if (av !== null && bv !== null && av > bv) {
        if (which === "min") min.value = String(bb);
        else max.value = String(aa);
      }
      rMin.value = String(aa);
      rMax.value = String(bb);
      writeHiddenFromPair(aa, bb);
      updateTrack(aa, bb);
      syncClear();
      syncSummaryChips();
      pickBestScrollerAndUpdate();
    };
    min.addEventListener("input", () => onTextInput("min"));
    max.addEventListener("input", () => onTextInput("max"));
    rMin.addEventListener("input", onRangeInput);
    rMax.addEventListener("input", onRangeInput);
    rMin.addEventListener("change", onRangeEnd);
    rMax.addEventListener("change", onRangeEnd);
    rMin.addEventListener("touchend", onRangeEnd);
    rMax.addEventListener("touchend", onRangeEnd);
    rMin.addEventListener("mouseup", onRangeEnd);
    rMax.addEventListener("mouseup", onRangeEnd);
    clear.addEventListener("click", () => {
      min.value = "";
      max.value = "";
      hidMin.value = "";
      hidMax.value = "";
      rMin.value = String(rangeMin);
      rMax.value = String(rangeMax);
      updateTrack(rangeMin, rangeMax);
      syncClear();
      syncSummaryChips();
      pickBestScrollerAndUpdate();
    });
    syncAllFromHidden();
    bar.appendChild(tag);
    bar.appendChild(fields);
    bar.appendChild(clear);
    bar.appendChild(slider);
    nodes.priceTopbar.appendChild(bar);
  };
  /* ---------------------------
     Sort UI
     --------------------------- */
  const renderSortAsOptions = (f, bodyMount) => {
    const ul = document.createElement("ul");
    ul.className = "af-of__optList";
    const hid = ensureHiddenInput(f.sort.name, f.sort.value);
    f.sort.options.forEach((o) => {
      const li = document.createElement("li");
      li.className = "af-of__optItem";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "af-of__optBtn af-of__sortBtn";
      btn.setAttribute("data-af-sort-name", f.sort.name);
      btn.setAttribute("data-af-sort-value", o.value);
      btn.classList.toggle("is-selected", hid.value === o.value);
      btn.setAttribute("aria-pressed", String(hid.value === o.value));
      btn.innerHTML = `
        <span class="af-of__optLeft">
          <span class="af-of__optMark" aria-hidden="true"></span>
          <span class="af-of__optLabelWrap">
            <span class="af-of__optLabel">${esc(o.label)}</span>
          </span>
        </span>
      `;
      btn.addEventListener("click", () => {
        hid.value = o.value;
        refreshOptionSelectedStates();
        syncSummaryChips();
        pickBestScrollerAndUpdate();
      });
      li.appendChild(btn);
      ul.appendChild(li);
    });
    bodyMount.appendChild(ul);
  };
  /* ---------------------------
     Render facet into subnav
     ✅ FIX: corrected querySelector syntax
     ⚡ Swipe gesture attached to subdrawer
     --------------------------- */
  const renderFacetIntoSubnav = (facetKey) => {
    const f = state.facets.find((x) => x.key === facetKey);
    if (!f || !nodes.subnav) return;
    state.activeKey = facetKey;
    root.classList.add(HAS_ACTIVE_CLASS);
    if (nodes.nav) {
      $$("[data-af-facet-key]", nodes.nav).forEach((b) => {
        b.classList.toggle(
          "is-active",
          b.getAttribute("data-af-facet-key") === facetKey
        );
      });
    }
    nodes.subnav.innerHTML = "";
    let bodyMount = nodes.subnav;
    if (isMobile()) {
      const head = document.createElement("div");
      head.className = "af-of__subhead";
      const back = document.createElement("button");
      back.type = "button";
      back.className = "af-of__backbtn";
      back.setAttribute("aria-label", "Back");
      back.innerHTML = '<span class="af-of__backchev" aria-hidden="true">‹</span>';
      back.addEventListener("click", () => closeSubdrawerInternal(false));
      const ht = document.createElement("div");
      ht.className = "af-of__subheadTitle";
      ht.textContent = f.label;
      head.appendChild(back);
      head.appendChild(ht);
      const body = document.createElement("div");
      body.className = "af-of__subbody";
      nodes.subnav.appendChild(head);
      nodes.subnav.appendChild(body);
      bodyMount = body;
      openSubdrawerInternal();

      // ⚡ Attach swipe-right-to-close on the subdrawer
      initSwipeGesture(nodes.subnav);
    } else {
      const title = document.createElement("div");
      title.className = "af-of__subTitle";
      title.textContent = f.label;
      bodyMount.appendChild(title);
    }
    if (f.type === "sort") {
      renderSortAsOptions(f, bodyMount);
      pickBestScrollerAndUpdate();
      return;
    }
    if (f.type === "options") {
      const ul = document.createElement("ul");
      ul.className = "af-of__optList";
      f.options.forEach((o) => {
        const li = document.createElement("li");
        li.className = "af-of__optItem";
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "af-of__optBtn";
        btn.setAttribute("data-af-name", o.name);
        btn.setAttribute("data-af-value", o.value);
        const exists = !!state.form.querySelector(
          'input[name="' + CSS.escape(o.name) + '"][value="' + CSS.escape(o.value) + '"]'
        );
        btn.classList.toggle("is-selected", exists);
        btn.setAttribute("aria-pressed", String(exists));
        btn.innerHTML = `
          <span class="af-of__optLeft">
            <span class="af-of__optMark" aria-hidden="true"></span>
            <span class="af-of__optLabelWrap">
              <span class="af-of__optLabel">${esc(o.label)}</span>
              ${o.count ? `<span class="af-of__optCount">${esc(o.count)}</span>` : ''}
            </span>
          </span>
        `;
        btn.addEventListener("click", () => {
          const existing = state.form.querySelector(
            'input[name="' + CSS.escape(o.name) + '"][value="' + CSS.escape(o.value) + '"]'
          );
          if (existing) {
            existing.remove();
            btn.classList.remove("is-selected");
            btn.setAttribute("aria-pressed", "false");
          } else {
            const i = document.createElement("input");
            i.type = "hidden";
            i.name = o.name;
            i.value = o.value;
            state.form.appendChild(i);
            btn.classList.add("is-selected");
            btn.setAttribute("aria-pressed", "true");
          }
          syncSummaryChips();
          pickBestScrollerAndUpdate();
        });
        li.appendChild(btn);
        ul.appendChild(li);
      });
      bodyMount.appendChild(ul);
      pickBestScrollerAndUpdate();
      return;
    }
  };
  /* ---------------------------
     Progress bar
     --------------------------- */
  const canScroll = (el) => {
    if (!el || el.nodeType !== 1) return false;
    const ch = el.clientHeight || 0;
    if (ch <= 0) return false;
    return el.scrollHeight > ch + 1;
  };
  const setProgressIdle = () => {
    const wrap = nodes.progWrap;
    if (!wrap) return;
    wrap.removeAttribute("hidden");
    wrap.classList.add("is-idle");
    wrap.style.setProperty("--af-prog", "0%");
  };
  const setProgressFrom = (el) => {
    const wrap = nodes.progWrap;
    if (!wrap) return;
    wrap.removeAttribute("hidden");
    if (!el || !canScroll(el)) {
      setProgressIdle();
      return;
    }
    const max = el.scrollHeight - el.clientHeight;
    const p = max <= 0 ? 0 : Math.max(0, Math.min(1, el.scrollTop / max));
    wrap.classList.remove("is-idle");
    wrap.style.setProperty("--af-prog", `${(p * 100).toFixed(2)}%`);
  };
  const scheduleProgress = (scroller) => {
    state.lastScrollTarget = scroller || state.lastScrollTarget;
    if (state.rafProg) return;
    state.rafProg = requestAnimationFrame(() => {
      state.rafProg = 0;
      const el = state.lastScrollTarget;
      if (el && canScroll(el)) {
        state.activeScrollEl = el;
        setProgressFrom(el);
      } else {
        pickBestScrollerAndUpdate();
      }
    });
  };
  const pickBestScrollerAndUpdate = () => {
    const subbody = $(".af-of__subbody", root);
    const candidates = [];
    if (root.classList.contains(SUBOPEN_CLASS) && subbody) candidates.push(subbody);
    if (nodes.subnav) candidates.push(nodes.subnav);
    if (nodes.nav) candidates.push(nodes.nav);
    const promos = $("[data-af-facets-promos]", root);
    if (promos) candidates.push(promos);
    const scroller = candidates.find((el) => canScroll(el));
    if (!scroller) {
      state.activeScrollEl = null;
      setProgressIdle();
      return;
    }
    state.activeScrollEl = scroller;
    setProgressFrom(scroller);
  };
  /* ---------------------------
     Scroll UX binding
     --------------------------- */
  const canScrollInDir = (el, dy) => {
    if (!el || !canScroll(el)) return false;
    const top = el.scrollTop;
    const max = el.scrollHeight - el.clientHeight;
    if (dy < 0) return top > 0;
    if (dy > 0) return top < max;
    return false;
  };
  const bindScrollUX = () => {
    if (state.scrollBound) return;
    state.scrollBound = true;
    root.addEventListener(
      "scroll",
      (e) => {
        if (!isOpen()) return;
        const t = e.target;
        if (t && t.nodeType === 1 && canScroll(t)) scheduleProgress(t);
      },
      true
    );
    root.addEventListener(
      "wheel",
      (e) => {
        if (!isOpen()) return;
        if (e.ctrlKey) return; // let pinch/ctrl-wheel zoom through
        const t = e.target;
        const insideScroller = t?.closest?.(
          ".af-of__subnav, .af-of__nav, .af-of__promos, .af-of__subbody"
        );
        if (insideScroller) {
          if (!canScrollInDir(insideScroller, e.deltaY)) {
            e.preventDefault();
            scheduleProgress(insideScroller);
          }
          return;
        }
        const s = state.activeScrollEl;
        if (!s || !canScroll(s)) {
          e.preventDefault();
          setProgressIdle();
          return;
        }
        e.preventDefault();
        const d =
          e.deltaMode === 1
            ? e.deltaY * 16
            : e.deltaMode === 2
            ? e.deltaY * s.clientHeight
            : e.deltaY;
        s.scrollTop += d;
        scheduleProgress(s);
      },
      { passive: false }
    );
    root.addEventListener(
      "touchmove",
      (e) => {
        if (!isOpen()) return;
        const allowPrice = e.target.closest(
          ".af-of__priceBar, .af-of__priceSlider, input.af-of__range, input.af-of__priceInput"
        );
        if (allowPrice) return;
        const inside = e.target.closest?.(
          ".af-of__subnav, .af-of__nav, .af-of__promos, .af-of__subbody"
        );
        if (inside) return;
        e.preventDefault();
      },
      { passive: false }
    );
  };
  /* =========================================================
     Quick filters + "For You" rail (personalized right column)
  ========================================================= */
  const lsArray = (key) => {
    try {
      const v = JSON.parse(localStorage.getItem(key) || "[]");
      return Array.isArray(v) ? v.filter(Boolean) : [];
    } catch {
      return [];
    }
  };
  const sizedUrl = (url, w) => {
    if (!url) return "";
    const base = url.replace(/[?&]width=\d+/g, "");
    return base + (base.includes("?") ? "&" : "?") + "width=" + w;
  };
  const countOf = (o) =>
    parseInt(String(o.count || "").replace(/[^\d]/g, ""), 10) || 0;

  /* ---- Personalized quick filters: track what the shopper actually uses ----
     A small localStorage log of applied filters powers a "recent + most used"
     chip bar. New shoppers (empty log) fall back to popular-by-count filters,
     so the bar is never empty. */
  const USAGE_KEY = "af_facet_usage";
  const loadUsage = () => lsArray(USAGE_KEY).filter((u) => u && u.name && u.value);
  const filterExists = (name, value) =>
    state.facets.some(
      (f) => f.type === "options" && f.options.some((o) => o.name === name && o.value === value)
    );
  const labelForFilter = (name, value) => {
    for (const f of state.facets) {
      if (f.type !== "options") continue;
      const o = f.options.find((x) => x.name === name && x.value === value);
      if (o) return o.label;
    }
    return prettyLabel(value);
  };
  // Called at Apply time: bump count + recency for each active option filter.
  const recordUsage = () => {
    if (!state.form) return;
    try {
      const usage = loadUsage();
      const idx = new Map(usage.map((u, i) => [u.name + "::" + u.value, i]));
      const now = Date.now();
      Array.from(state.form.querySelectorAll('input[type="hidden"]')).forEach((i) => {
        if (!i.name || !i.value) return;
        if (i.name.includes("sort_by") || i.name.includes("availability")) return;
        if (SCOPE_PARAMS.includes(i.name)) return;
        if (
          state.priceFacet &&
          (i.name === state.priceFacet.price.minName ||
            i.name === state.priceFacet.price.maxName)
        )
          return;
        const key = i.name + "::" + i.value;
        const label = labelForFilter(i.name, i.value);
        if (idx.has(key)) {
          const u = usage[idx.get(key)];
          u.count = (u.count || 0) + 1;
          u.last = now;
          u.label = label;
        } else {
          usage.push({ name: i.name, value: i.value, label, count: 1, last: now });
          idx.set(key, usage.length - 1);
        }
      });
      localStorage.setItem(USAGE_KEY, JSON.stringify(usage.slice(-40)));
    } catch {}
  };

  // Chip bar (horizontally scrollable): last-used → most-used → popular fallback,
  // deduped and limited to filters valid for the CURRENT collection. Max 8.
  const buildQuickChips = () => {
    const out = [];
    const seen = new Set();
    const add = (name, value, label) => {
      const k = name + "::" + value;
      if (seen.has(k) || out.length >= 8) return;
      if (!filterExists(name, value)) return;
      seen.add(k);
      out.push({ name, value, label });
    };
    const usage = loadUsage();
    // 1) Last used (recency-first)
    usage
      .slice()
      .sort((a, b) => (b.last || 0) - (a.last || 0))
      .forEach((u) => add(u.name, u.value, u.label));
    // 2) Most used (frequency) — backfill
    usage
      .slice()
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .forEach((u) => add(u.name, u.value, u.label));
    // 3) Popular fallback — top option per facet by product count
    const perFacet = [];
    for (const f of state.facets) {
      if (f.type !== "options" || /avail/i.test(f.label)) continue;
      const top = f.options
        .map((o) => ({ o, n: countOf(o) }))
        .sort((a, b) => b.n - a.n)[0];
      if (top && top.n > 0) {
        perFacet.push({ name: top.o.name, value: top.o.value, label: top.o.label, n: top.n });
      }
    }
    perFacet.sort((a, b) => b.n - a.n);
    perFacet.forEach((p) => add(p.name, p.value, p.label));
    return out;
  };

  const toggleQuickFilter = (name, value, btn) => {
    if (!state.form) return;
    const existing = state.form.querySelector(
      'input[name="' + CSS.escape(name) + '"][value="' + CSS.escape(value) + '"]'
    );
    if (existing) {
      existing.remove();
      btn.classList.remove("is-selected");
      btn.setAttribute("aria-pressed", "false");
    } else {
      const i = document.createElement("input");
      i.type = "hidden";
      i.name = name;
      i.value = value;
      state.form.appendChild(i);
      btn.classList.add("is-selected");
      btn.setAttribute("aria-pressed", "true");
    }
    refreshOptionSelectedStates();
    syncSummaryChips();
    pickBestScrollerAndUpdate();
  };

  const renderQuickFilters = () => {
    if (!nodes.quickbar || !nodes.quickChips) return;
    const chips = buildQuickChips();
    nodes.quickChips.innerHTML = "";
    if (!chips.length) {
      nodes.quickbar.hidden = true;
      return;
    }
    chips.forEach((c) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "af-of__quickChip";
      b.setAttribute("data-af-quick", "");
      b.setAttribute("data-af-name", c.name);
      b.setAttribute("data-af-value", c.value);
      const on = !!state.form?.querySelector(
        'input[name="' + CSS.escape(c.name) + '"][value="' + CSS.escape(c.value) + '"]'
      );
      b.classList.toggle("is-selected", on);
      b.setAttribute("aria-pressed", String(on));
      b.textContent = c.label;
      b.addEventListener("click", () => toggleQuickFilter(c.name, c.value, b));
      nodes.quickChips.appendChild(b);
    });
    nodes.quickbar.hidden = false;
  };

  // "For You" cascade: recently viewed → wishlist → collection bestsellers.
  const forYouRecent = async () => {
    const handles = lsArray("af_recently_viewed_handles").slice(0, 6);
    if (!handles.length) return null;
    const items = [];
    for (const h of handles) {
      try {
        const r = await fetch("/products/" + encodeURIComponent(h) + ".js", {
          credentials: "same-origin",
        });
        if (!r.ok) continue;
        const p = await r.json();
        items.push({
          url: p.url || "/products/" + p.handle,
          image: p.featured_image || (p.images && p.images[0]) || "",
          title: p.title || "",
          vendor: p.vendor || "",
        });
      } catch {}
      if (items.length >= 4) break;
    }
    return items.length ? { label: "Pick up where you left off", items } : null;
  };

  const forYouWishlist = () => {
    const wl = lsArray("aromatica_wishlist").filter((x) => x && x.handle);
    if (!wl.length) return null;
    const items = wl.slice(0, 4).map((x) => ({
      url: x.url || "/products/" + x.handle,
      image: x.image || "",
      title: x.title || x.handle,
      vendor: x.vendor || "",
    }));
    return items.length ? { label: "From your wishlist", items } : null;
  };

  const forYouCollection = () => {
    const grid = document.querySelector(
      "#product-grid, .product-grid, .collection .grid, ul.grid"
    );
    if (!grid) return null;
    const items = [];
    const seen = new Set();
    grid.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      if (items.length >= 4) return;
      const url = a.getAttribute("href");
      if (!url || seen.has(url)) return;
      const card = a.closest("li, .card-wrapper, .grid__item") || a;
      const img = card.querySelector("img");
      const title = (
        card.querySelector(
          ".card__heading, .full-unstyled-link, .card-information__text"
        )?.textContent ||
        a.textContent ||
        ""
      ).trim();
      if (!title) return;
      seen.add(url);
      items.push({
        url,
        image: img ? img.getAttribute("src") || img.currentSrc || "" : "",
        title,
        vendor: "",
      });
    });
    if (!items.length) return null;
    let heading = (
      document.querySelector(".collection-hero__title, .title--primary, main h1")
        ?.textContent || ""
    )
      .replace(/\s+/g, " ")
      .trim();
    heading = heading.replace(/^collection\b[:\s-]*/i, "").trim();
    return { label: heading ? "Popular in " + heading : "Popular right now", items };
  };

  let _forYouLoading = false;
  const renderForYou = async () => {
    if (!nodes.forYou || !nodes.forYouInner || _forYouLoading) return;
    _forYouLoading = true;
    try {
      let data = await forYouRecent();
      if (!data) data = forYouWishlist();
      if (!data) data = forYouCollection();
      if (!data || !data.items.length) {
        nodes.forYou.hidden = true;
        nodes.forYouInner.innerHTML = "";
        return;
      }
      const cards = data.items
        .map(
          (it) => `
        <a class="af-of__fyCard" href="${esc(it.url)}">
          <span class="af-of__fyMedia">${
            it.image
              ? `<img src="${esc(sizedUrl(it.image, 200))}" srcset="${esc(
                  sizedUrl(it.image, 200)
                )} 200w, ${esc(sizedUrl(it.image, 320))} 320w" sizes="120px" alt="" loading="lazy" decoding="async">`
              : ""
          }</span>
          <span class="af-of__fyText">
            ${it.vendor ? `<span class="af-of__fyVendor">${esc(it.vendor)}</span>` : ""}
            <span class="af-of__fyTitle">${esc(it.title)}</span>
          </span>
        </a>`
        )
        .join("");
      nodes.forYouInner.innerHTML =
        `<div class="af-of__foryouHead">${esc(data.label)}</div>` +
        `<div class="af-of__fyList">${cards}</div>`;
      nodes.forYou.hidden = false;
    } finally {
      _forYouLoading = false;
    }
  };

  /* ---------------------------
     Build UI
     --------------------------- */
  const buildUI = () => {
    ensureProgressEls();
    parseDawnFacets();
    renderNav();
    renderPriceTopbar();
    syncSummaryChips();
    refreshOptionSelectedStates();
    renderQuickFilters();
    bindScrollUX();
    pickBestScrollerAndUpdate();
  };
  const onClearAll = () => {
    // Clear = remove every applied filter and show all products. buildUI()
    // re-seeds the form from the page's active facets (parseDawnFacets), so a
    // pending-only clear would be undone instantly — commit by navigating to
    // the unfiltered URL, keeping only the current sort (same model as Apply).
    const sort = getDawnSortSelect();
    const params = new URLSearchParams(getScopeParams());
    if (sort && sort.name && sort.value) params.set(sort.name, sort.value);
    const qs = params.toString();
    leaveTo(window.location.pathname + (qs ? "?" + qs : ""));
  };
  const onApply = () => {
    if (!state.form) return;
    recordUsage();
    // Empty values (an unset price bound, the default sort) only add noise to the URL.
    const q = new URLSearchParams();
    new FormData(state.form).forEach((v, k) => {
      if (v !== "") q.append(k, v);
    });
    const qs = q.toString();
    leaveTo(window.location.pathname + (qs ? "?" + qs : ""));
  };
  // Apply and Clear load a new page. Step back over the overlay's own history
  // entries first, so Back from the results goes to the page before, not to a
  // leftover overlay entry that does nothing.
  const leaveTo = (url) => {
    const st = history.state;
    const n = isOurHistState(st) ? Number(st.level || 0) : 0;
    if (!n) {
      window.location.assign(url);
      return;
    }
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      window.location.assign(url);
    };
    window.addEventListener("popstate", go, { once: true });
    setTimeout(go, 1000);
    history.go(-n);
  };
  /* ---------------------------
     Open / Close
     --------------------------- */
  const openInternal = () => {
    closeNativeMobileDetails();
    hardUnlockScroll(true);
    root.hidden = false;
    state.activeKey = null;
    root.classList.remove(HAS_ACTIVE_CLASS);
    root.classList.remove(SUBOPEN_CLASS);
    root.classList.add(OPEN_CLASS);
    root.style.touchAction = "pan-y";
    lockScroll();
    buildUI();
    if (nodes.subnav) {
      nodes.subnav.innerHTML = '<div class="af-of__subEmpty">Select a filter</div>';
    }
    if (state.histLevel < 1) pushHist(1);

    // ⚡ Start promo videos now that overlay is visible
    playPromoVideos();
    renderForYou();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        pickBestScrollerAndUpdate();
      });
    });
    setTimeout(() => {
      try {
        nodes.close && nodes.close.focus({ preventScroll: true });
      } catch {}
    }, 0);
  };
  const closeInternal = (fromPop) => {
    root.classList.remove(OPEN_CLASS);
    root.classList.remove(HAS_ACTIVE_CLASS);
    root.classList.remove(SUBOPEN_CLASS);
    closeNativeMobileDetails();
    root.style.touchAction = "";

    // ⚡ Pause promo videos to save resources
    pausePromoVideos();

    unlockScroll();
    if (!fromPop && state.histArmed && currentHistIsOursAtOrAbove(1)) {
      requestBackOne();
    }
    // Restore focus to whatever opened the overlay (keyboard/a11y).
    const rf = state.returnFocusEl;
    state.returnFocusEl = null;
    if (rf && typeof rf.focus === "function") {
      setTimeout(() => {
        try { rf.focus({ preventScroll: true }); } catch {}
      }, 0);
    }
    if (state.hideT) window.clearTimeout(state.hideT);
    state.hideT = window.setTimeout(() => {
      state.hideT = 0;
      if (!isOpen()) {
        root.hidden = true;
        state.histLevel = 0;
        state.histArmed = false;
        closeNativeMobileDetails();
        hardUnlockScroll();
      }
    }, HIDE_DELAY_MS);
  };
  const open = () => {
    if (state.hideT) {
      window.clearTimeout(state.hideT);
      state.hideT = 0;
    }
    openInternal();
  };
  const close = () => closeInternal(false);
  /* ---------------------------
     Events
     --------------------------- */
  nodes.close?.addEventListener("click", () => close());
  nodes.apply?.addEventListener("click", onApply);
  nodes.clearAll?.addEventListener("click", onClearAll);
  root.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-af-facet-key]");
    if (!btn) return;
    const key = btn.getAttribute("data-af-facet-key");
    if (!key) return;
    renderFacetIntoSubnav(key);
  });
  window.addEventListener("keydown", (e) => {
    if (!isOpen()) return;
    if (e.key === "Tab") {
      trapTabKey(e);
      return;
    }
    if (e.key !== "Escape") return;
    if (root.classList.contains(SUBOPEN_CLASS)) {
      e.preventDefault();
      closeSubdrawerInternal(false);
      return;
    }
    close();
  });
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target.closest("[data-af-facets-toggle]");
      if (!t) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      closeNativeMobileDetails();
      hardUnlockScroll(true);
      state.returnFocusEl = t;
      open();
    },
    true
  );
  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const t = e.target.closest?.("[data-af-facets-toggle]");
      if (!t) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      closeNativeMobileDetails();
      hardUnlockScroll(true);
      state.returnFocusEl = t;
      open();
    },
    true
  );
  window.addEventListener("resize", () => {
    if (!isOpen()) return;
    pickBestScrollerAndUpdate();
  });
  root.addEventListener("transitionend", () => {
    if (!isOpen()) return;
    pickBestScrollerAndUpdate();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !isOpen()) {
      closeNativeMobileDetails();
      hardUnlockScroll();
    }
  });
  window.addEventListener("pageshow", () => {
    if (!isOpen()) {
      closeNativeMobileDetails();
      hardUnlockScroll();
    }
  });
  window.addEventListener("pagehide", () => {
    hardUnlockScroll();
  });
  hardUnlockScroll();
})();
